import React, { useCallback } from 'react';
import { Tooltip } from 'syngenta-digital-cropwise-react-ui-kit';
import IntegrationIcon from 'assets/IntegrationIcon';

import { formatDate } from 'screens/Applications&WorkOrders/Components/helpers';
import HighlightedText from 'components/HighlightedText';
import RecordsCreatedIndicators from 'components/RecordsCreatedIndicators';
import { buildRecordIndicators } from 'components/RecordsCreatedIndicators/helpers';
import FieldsDisplay from './Components/FieldsDisplay';
import TagsRow from './Components/TagsRow';
import { OperationSummary } from '../types';

interface RowItemProps {
  operation: OperationSummary;
  isSelected?: boolean;
  allTags?: Array<{ id: string; name: string; color: string }>;
  searchTerm?: string;
}

const SEARCH_HIGHLIGHT_COLOR = '#C6ECFF';
const SEARCH_HIGHLIGHT_SELECTED_COLOR = '#97C7ED';

const TOOLTIP_TEXT = {
  JOHN_DEERE: 'from John Deere',
  AGFINITI: 'from AgFinity',
  FARMOBILE: 'from Farmobile',
  CNHI: 'from CNH',
  TRIMBLE_AGRICULTURE: 'from Trimble',
  OTHER: 'via File Upload'
};

const RowItem: React.FC<RowItemProps> = ({
  operation,
  isSelected,
  allTags = [],
  searchTerm = ''
}) => {
  const getIntegrationTooltip = useCallback((integrationType?: string) => {
    const tooltipText =
      TOOLTIP_TEXT[integrationType as keyof typeof TOOLTIP_TEXT] ||
      'via Integration';

    return `Imported ${tooltipText}`;
  }, []);

  const isIntegration =
    operation.recordSource?.recordSourceType === 'INTEGRATIONS';
  const applicationIndicators = buildRecordIndicators(
    operation.childrenRecords,
    ['APPLICATION']
  );

  return (
    <div className="application-item-content w-full flex flex-col justify-center items-start relative">
      {isSelected && (
        <div
          style={{
            width: '4px',
            position: 'absolute',
            top: '-9px',
            bottom: '-9px',
            left: '-16px',
            borderRadius: '0 4px 4px 0',
            background: '#0092E4'
          }}
        />
      )}
      <div className="application-name text-left text-base font-normal text-gray-900 w-full flex items-center justify-between">
        <HighlightedText
          text={operation.name}
          searchTerm={searchTerm}
          isSelected={isSelected}
          highlightColor={SEARCH_HIGHLIGHT_COLOR}
          selectedHighlightColor={SEARCH_HIGHLIGHT_SELECTED_COLOR}
          className="truncate flex-1 block"
        />
        {(isIntegration || applicationIndicators.length > 0) && (
          <div className="shrink-0 ml-2 flex items-center gap-2">
            {isIntegration && (
              <Tooltip
                title={getIntegrationTooltip(
                  operation.recordSource?.recordSourceAdditionalInfo
                )}
                placement="top"
                color="#232630"
              >
                <IntegrationIcon />
              </Tooltip>
            )}
            <RecordsCreatedIndicators
              indicators={applicationIndicators}
              tooltipArrowBottomRight
            />
          </div>
        )}
      </div>

      <div className="flex items-end w-full gap-4">
        <div className="application-datetime shrink-0">
          <div className="text-xs font-normal" style={{ color: '#696F88' }}>
            Start Date & Time
          </div>
          <div className="text-sm text-gray-900">
            {operation.startDate
              ? formatDate(operation.startDate)
              : 'No date available'}
          </div>
        </div>
        {operation.fieldsBrief && (
          <div className="text-left shrink min-w-0">
            <FieldsDisplay
              fields={operation.fieldsBrief.firstFields || []}
              firstFieldInfo={operation.fieldsBrief.firstFieldInfo || {}}
              totalFields={operation.fieldsBrief.totalFields || 0}
              showSubheader
              iconWidth={14}
              iconHeight={14}
              iconColor="#868CA2"
              iconBorderColor="#868CA2"
              disablePositionOffset
              isSelected={isSelected}
            />
          </div>
        )}
      </div>

      {operation.tagIds && operation.tagIds.length > 0 && (
        <div style={{ marginTop: '8px', width: '100%' }}>
          <TagsRow tagIds={operation.tagIds} allTags={allTags} />
        </div>
      )}
    </div>
  );
};

export default RowItem;
